import { createSlice, createAction, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { Basemap, CartoKeplerMapConfigEnvelope, Formats } from 'features/maps/types'
import { WrapperWidgetUIProps } from '@carto/meridian-ds/widgets'
import { selectValidSpatialFilter } from '@carto/react-redux'

import { KeplerGlSchema } from '@carto/kepler.gl'
import { MapState } from '@carto/kepler.gl/reducers'

import { ApiError, RequestStatus } from 'features/common'
import { getValueHash } from 'features/common/utils/cryptoHash'
import { ImportingJob } from 'features/jobs/types'
import { BuilderUiPersistenceState, CustomBaseMapSettings, SqlParameter } from 'features/maps/types'
import {
  AttributeStatsRequest,
  LayerRequest,
  FeatureDetails,
  TakeOverResponseState,
  BuilderLockState,
  RightPaneTab,
  WidgetType,
  BuilderMode,
  MeasureTooltip,
  InitBuilderRequestParams,
  Widget,
  LegendSettings,
  LayerLegendSettings,
  PopupSettings,
  LayerPopupSettings,
  SQLPanelProps,
  WidgetsMapConfig,
  BuilderDataset,
  AISettings,
  BuilderGenAIThread,
  BuilderGenAIConversation,
  BuilderGenAIAuthor,
  BuilderGenAIAction,
  DynamicTilingMigrationBackup,
  DEFAULT_MAP_INDEX,
  MapDatasetEnriched
} from 'features/builder/types'
import { RootState } from 'app/state/store'
import { Connection, ProviderType } from 'features/connections'
import { FeatureCollection } from '@deck.gl-community/editable-layers'
import { BasemapSettings } from 'features/account/types'
import { defaultBasemapsSettings } from '../ui/builderConstants'
import { KeplerMapStyle } from '../ui/KeplerGl/types'
import { DEFAULT_MAP_STYLE } from '../ui/KeplerGl/defaultMapStyles'
import { defaultMapStyleSelector } from '../ui/KeplerGl/mapStyles'
import { getFiltersWithoutTimeType } from './widgetsUtils'
import { updateLayerCustomMarkers } from './updateLayerCustomMarkers'
import { Layer } from '@carto/kepler.gl/layers'
import { getNewDatasetColor } from './datasetUtils'
import { blockAutoSaveDocMigrationTemporary } from '../model/documentModeMigration'

export const name = 'builder'

interface MeasureToolPopup {
  x: number
  y: number
  totalDistance: string
  totalDistanceAlt?: string
}

export interface BuilderState extends BuilderUiPersistenceState {
  isLoading: boolean
  saveNeeded: boolean
  saveMapError?: ApiError
  sqlPanel?: SQLPanelProps
  draftQuery?: string
  builderMode: BuilderMode
  previewMode: boolean
  lastThumbnailMapConfigHash?: string
  widgets: Widget[]
  selectedWidgetId?: string
  customBaseMaps?: CustomBaseMapSettings
  legendSettings?: LegendSettings
  popupSettings?: PopupSettings
  datasetAttributeStatsOnRequest: Record<string, Array<string>>
  featureDetailsPanel?: FeatureDetails
  sqlParameters: SqlParameter[]
  currentMapLockStatus?: BuilderLockState
  takeOverResponseState: TakeOverResponseState
  layersUpdateStatus: Record<string, RequestStatus>
  viewerCredentialsRequired?: Connection[]
  userLocation?: GeoJSON.Position
  isMeasureToolEnabled?: boolean
  measureToolLayer?: FeatureCollection
  measureToolTooltips?: MeasureTooltip[]
  measureToolGuides?: FeatureCollection
  measureToolPopup?: MeasureToolPopup
  timeSeriesDataSourceAnimating: Record<string, boolean>
  mapPasswordRequired?: boolean
  mapPasswordEnterAttempt?: number
  geocoderSearchText?: string
  basemapSettings: BasemapSettings
  customBasemaps: Basemap[]
  mapStyles: KeplerMapStyle[]
  layerPanelLoadingStatus: RequestStatus
  mapStylesStatus: RequestStatus
  takeMapScreenshots: boolean
  // In split mode we have two maps
  mapScreenshots: string[]
  aiSettings: AISettings
  genAiThread?: BuilderGenAIThread
  genAiConversation: BuilderGenAIConversation
  dynamicTilingMigrationBackup?: DynamicTilingMigrationBackup
  dragMapState?: Partial<MapState>
  tilesetWidgetSourceReady?: Record<string, boolean>
  highlighted?: {
    id: string | null
    type: 'dataset' | 'layer' | null
  }
}

export enum QueryActions {
  Update,
  Cancel
}
export interface UpdateQueryProps {
  mapId: string
  datasetId: string
  query: string
  sqlParameters?: SqlParameter[]

  format?: Formats
  provider?: ProviderType
  geoColumn?: string | null
  columns?: string[] | null

  queryAction?: QueryActions
  skipSaveDataset?: boolean
  convertToQuery?: boolean
}

const initialState: BuilderState = {
  mapPasswordRequired: false,
  builderMode: BuilderMode.Editor,
  previewMode: false,
  isLoading: true,
  saveNeeded: false,
  widgets: [],
  controlsPaneOpen: false,
  datasetAttributeStatsOnRequest: {},
  sqlParameters: [],
  takeOverResponseState: { active: false },
  layersUpdateStatus: {},
  descriptionOpen: false,
  descriptionPreview: false,
  userLocation: undefined,
  isMeasureToolEnabled: false,
  measureToolLayer: undefined,
  measureToolTooltips: undefined,
  measureToolGuides: undefined,
  timeSeriesDataSourceAnimating: {},
  basemapSettings: defaultBasemapsSettings,
  customBasemaps: [],
  mapStyles: [],
  mapStylesStatus: RequestStatus.Idle,
  layerPanelLoadingStatus: RequestStatus.Idle,
  takeMapScreenshots: false,
  mapScreenshots: [],
  aiSettings: {
    enabled: false,
    mapContext: '',
    conversationStarters: [],
    userGuideEnabled: false,
    userGuide: ''
  },
  genAiConversation: []
}

export const ADD_FILTER_ACTION = 'carto/addFilter'
export const REMOVE_FILTER_ACTION = 'carto/removeFilter'
export const CLEAR_FILTERS_ACTION = 'carto/clearFilters'
export const ADD_SPATIAL_FILTER_ACTION = 'carto/addSpatialFilter'
export const REMOVE_SPATIAL_FILTER_ACTION = 'carto/removeSpatialFilter'
export const SET_IS_DROPPING_FEATURES = 'carto/setIsDroppingFeatures' // TODO ensure proper export from C4R
export const SET_GEOCODER_RESULT = 'carto/setGeocoderResult'

const asyncActions = {
  initBuilderMap: createAction<InitBuilderRequestParams>(`${name}/init_builder_map`),

  addLayerRequest: createAction<LayerRequest>(`${name}/add_layer_request`),
  addLayerToExistingDatasetRequest: createAction<string>(`${name}/add_layer_to_existing_dataset_request`),

  addQueryRequest: createAction<LayerRequest>(`${name}/add_query_request`),
  updateQueryRequest: createAction<UpdateQueryProps>(`${name}/update_query_request`),
  cancelQueryRequest: createAction<Partial<UpdateQueryProps>>(`${name}/cancel_query_request`),

  refreshDatasetRequest: createAction<string>(`${name}/refresh_dataset_request`),
  refreshDatasetsByConnections: createAction<string[]>(`${name}/refresh_datasets_by_connections`),

  attachViewerCredentials: createAction(`${name}/attach_viewer_credentials`),
  addLayerFromImportedJobRequest: createAction<ImportingJob>(`${name}/add_layer_for_imported_job_request`),
  newWidget: createAction<Widget['dataSource']>(`${name}/new_widget`),
  removeFilterByWidgetRequest: createAction<Widget>(`${name}/remove_filter_by_widget_request`),
  calculateFeaturesForTilesetRequest: createAction<{ layerId: string }>(`${name}/calculate_features_for_tileset`),
  loadAttributeStatsRequest: createAction<AttributeStatsRequest>(`${name}/load_attribute_stats`),
  loadHexColumnStatsRequest: createAction<AttributeStatsRequest>(`${name}/load_hex_column_stats`),
  forceCalculateDatasourceFeatures: createAction<string>(`${name}/force_calculate_datasource_features`),
  updateSpatialIndexAggregationRequest: createAction<string>(`${name}/update_spatial_index_aggregation`),
  updateDatasetsWithSqlParametersRequest: createAction<{ parameter?: SqlParameter; skipSaveDataset?: boolean }>(
    `${name}/updateDatasetsWithSqlParametersRequest`
  ),
  unlockMapRequest: createAction<string>(`${name}/unlock_map_request`),
  updateSpatialIndexAggregationResLevelRequest: createAction<{ datasetId: string; aggregationResLevel: number }>(
    `${name}/update_spatial_index_aggregation_res_level`
  ),
  queryThisTableRequest: createAction<{ datasetId: string }>(`${name}/query_this_table_request`),
  convertTableToQueryDatasetRequest: createAction<{ datasetId: string; sqlQuery: string; geoColumn: string }>(
    `${name}/convert_table_to_query_request`
  ),
  aggregateDatasetByGeometry: createAction<{
    datasetId: string
    layerId: string
    geometry: string
    aggregate: boolean
  }>(`${name}/aggregate_dataset_by_geometry`),
  updateLayerFiltersForTimeSeriesAnimationRequest: createAction<{ datasetId: string; isRunning: boolean }>(
    `${name}/configure_filters_for_time_series_animation_request`
  ),
  mapPasswordUpdated: createAction(`${name}/map_password_updated`),
  legendVisibilityChange: createAction<{ layerId: string; isVisible: boolean }>(`${name}/legend_visibility_change`)
}
export const showGlobalBuilderError = createAction<string>(`${name}/show_global_error`)

let nextNewSqlPanelKey = 0

const reducers = {
  setMapPasswordRequired: (state: BuilderState, action: PayloadAction<boolean>) => {
    state.mapPasswordRequired = action.payload
  },
  incMapPasswordEnterAttempt: (state: BuilderState) => {
    state.mapPasswordEnterAttempt = (state.mapPasswordEnterAttempt ?? 0) + 1
  },
  showSQLPanel: (state: BuilderState, action: PayloadAction<SQLPanelProps>) => {
    state.sqlPanel = action.payload
    if (!state.sqlPanel.key) {
      state.sqlPanel.key = state.sqlPanel.datasetId || `new-sql-query-${String(nextNewSqlPanelKey++)}`
    }
    state.draftQuery = action.payload.query
  },
  setSQLPanelMinimized: (state: BuilderState, action: PayloadAction<boolean>) => {
    if (state.sqlPanel) {
      state.sqlPanel.minimized = action.payload
    }
  },
  hideSQLPanel: (state: BuilderState) => {
    state.sqlPanel = undefined
    state.draftQuery = undefined
  },
  setDraftQuery: (state: BuilderState, action: PayloadAction<string>) => {
    state.draftQuery = action.payload
  },
  resetBuilderState: () => {
    return initialState
  },
  setIsLoading: (state: BuilderState, action: PayloadAction<boolean>) => {
    state.isLoading = action.payload
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setSaveNeeded: (state: BuilderState, _action: PayloadAction<void>) => {
    state.saveNeeded = true
  },
  resetSaveNeeded: (state: BuilderState) => {
    state.saveNeeded = false
  },
  setSaveMapError: (state: BuilderState, action: PayloadAction<ApiError>) => {
    state.saveMapError = action.payload
  },
  setBuilderMode: (state: BuilderState, action: PayloadAction<BuilderMode>) => {
    state.builderMode = action.payload
  },
  setPreviewMode: (state: BuilderState, action: PayloadAction<boolean>) => {
    const newPreviewMode = action.payload
    state.previewMode = newPreviewMode
    state.builderMode = newPreviewMode ? BuilderMode.Viewer : BuilderMode.Editor
  },
  setLastThumbnailMapConfigHash: (state: BuilderState, action: PayloadAction<string>) => {
    state.lastThumbnailMapConfigHash = action.payload
  },
  setWidgets: (state: BuilderState, action: PayloadAction<Widget[]>) => {
    state.widgets = action.payload
  },
  removeWidget: (state: BuilderState, action: PayloadAction<Widget>) => {
    const { id } = action.payload
    state.widgets = state.widgets.filter((w) => w.id !== id)

    if (state.selectedWidgetId === id) {
      state.selectedWidgetId = undefined
    }
  },
  removeWidgetsByDataSource: (state: BuilderState, action: PayloadAction<Widget['dataSource']>) => {
    state.widgets = state.widgets.filter((w) => w.dataSource !== action.payload)
  },
  updateWidgetProps: (
    state: BuilderState,
    action: PayloadAction<{ id: Widget['id']; props: Record<string, unknown> }>
  ) => {
    const { id, props } = action.payload
    const idx = state.widgets.findIndex((w) => w.id === id)

    if (idx >= 0) {
      state.widgets[idx] = { ...state.widgets[idx], ...props }
    }
  },
  updateWidgetWrapperProps: (
    state: BuilderState,
    action: PayloadAction<{ id: string; wrapperProps: Partial<WrapperWidgetUIProps> }>
  ) => {
    const { id, wrapperProps } = action.payload
    const idx = state.widgets.findIndex((w) => w.id === id)

    if (idx >= 0) {
      state.widgets[idx].wrapperProps = { ...state.widgets[idx].wrapperProps, ...wrapperProps }
    }
  },
  replaceWidget: (state: BuilderState, action: PayloadAction<Widget>) => {
    const widget = action.payload
    const idx = state.widgets.findIndex((w) => w.id === widget.id)

    if (idx >= 0) {
      state.widgets[idx] = widget
    }
  },
  setSelectedWidgetId: (state: BuilderState, action: PayloadAction<string | undefined>) => {
    const widgetId = action.payload
    state.selectedWidgetId = action.payload
    if (widgetId) {
      state.descriptionOpen = false
      state.controlsPaneOpen = true
      state.controlsPaneTab = RightPaneTab.Widgets
    }
  },
  setTilesetWidgetSourceReady: (state: BuilderState, action: PayloadAction<{ sourceId: string; ready: boolean }>) => {
    const { sourceId, ready } = action.payload

    state.tilesetWidgetSourceReady = {
      ...state.tilesetWidgetSourceReady,
      [sourceId]: ready
    }
  },
  setControlsPaneOpen: (state: BuilderState, action: PayloadAction<boolean>) => {
    state.controlsPaneOpen = action.payload
  },
  setControlsPaneState: (state: BuilderState, action: PayloadAction<{ open: boolean; tab?: RightPaneTab }>) => {
    state.controlsPaneOpen = action.payload.open
    if (action.payload.open && state.descriptionOpen) {
      state.descriptionOpen = false
    }
    if (action.payload.tab) {
      state.controlsPaneTab = action.payload.tab
    }
  },
  setRightPaneTab: (state: BuilderState, action: PayloadAction<RightPaneTab>) => {
    state.controlsPaneTab = action.payload
  },
  setDescriptionOpen(state: BuilderState, action: PayloadAction<boolean>) {
    state.descriptionOpen = action.payload
  },
  setDescriptionPreview(state: BuilderState, action: PayloadAction<boolean>) {
    state.descriptionPreview = action.payload
  },
  setCustomBaseMap: (state: BuilderState, action: PayloadAction<Partial<CustomBaseMapSettings>>) => {
    state.customBaseMaps = {
      ...state.customBaseMaps,
      ...action.payload
    }
  },
  setCustomBaseMapSettings: (state: BuilderState, action: PayloadAction<CustomBaseMapSettings>) => {
    state.customBaseMaps = action.payload
  },
  setLegendSettings: (state: BuilderState, action: PayloadAction<LegendSettings>) => {
    state.legendSettings = action.payload
  },
  setLayerLegendSettings: (
    state: BuilderState,
    action: PayloadAction<{ layerId: string; settings: LayerLegendSettings }>
  ) => {
    state.legendSettings = {
      ...state.legendSettings,
      expanded: state.legendSettings?.expanded || { [DEFAULT_MAP_INDEX]: false },
      layers: {
        ...state.legendSettings?.layers,
        [action.payload.layerId]: action.payload.settings
      }
    }
  },
  setPopupSettings: (state: BuilderState, action: PayloadAction<PopupSettings>) => {
    state.popupSettings = action.payload
  },
  setLayerPopupSettings: (
    state: BuilderState,
    action: PayloadAction<{ layerId: string; settings: LayerPopupSettings }>
  ) => {
    state.popupSettings = {
      ...state.popupSettings,
      layers: {
        ...state.popupSettings?.layers,
        [action.payload.layerId]: action.payload.settings
      }
    }
  },
  moveLayerPopupSettings: (state: BuilderState, action: PayloadAction<{ oldLayerId: string; newLayerId: string }>) => {
    const { oldLayerId, newLayerId } = action.payload
    const movedSettings = state.popupSettings?.layers[oldLayerId]
    if (movedSettings) {
      delete state.popupSettings?.layers[oldLayerId]
      if (!state.popupSettings) {
        state.popupSettings = { layers: {} }
      }
      state.popupSettings.layers[newLayerId] = movedSettings
    }
  },
  setFeatureDetailsPanel: (state: BuilderState, action: PayloadAction<FeatureDetails | undefined>) => {
    state.featureDetailsPanel = action.payload
  },
  closeFeatureDetailsPanel: (state: BuilderState, action: PayloadAction<{ fromMapViewId: number }>) => {
    if (state.featureDetailsPanel?.mapViewId === action.payload.fromMapViewId) {
      state.featureDetailsPanel = undefined
    }
  },
  addDatasetAttributeStatsOnRequest: (
    state: BuilderState,
    action: PayloadAction<{ datasetId: string; attribute: string }>
  ) => {
    const { datasetId, attribute } = action.payload

    if (!state.datasetAttributeStatsOnRequest[datasetId]) {
      state.datasetAttributeStatsOnRequest[datasetId] = []
    }
    state.datasetAttributeStatsOnRequest[datasetId].push(attribute)
  },
  removeDatasetAttributeStatsOnRequest: (
    state: BuilderState,
    action: PayloadAction<{ datasetId: string; attribute: string }>
  ) => {
    const { datasetId, attribute } = action.payload

    if (!state.datasetAttributeStatsOnRequest[datasetId]) {
      return
    }
    state.datasetAttributeStatsOnRequest[datasetId] = state.datasetAttributeStatsOnRequest[datasetId].filter(
      (attr) => attr !== attribute
    )

    if (state.datasetAttributeStatsOnRequest[datasetId].length === 0) {
      delete state.datasetAttributeStatsOnRequest[datasetId]
    }
  },
  loadSqlParameters: (state: BuilderState, action: PayloadAction<SqlParameter[]>) => {
    state.sqlParameters = action.payload
  },
  addSqlParameter: (state: BuilderState, action: PayloadAction<SqlParameter>) => {
    state.sqlParameters.push(action.payload)
    state.controlsPaneOpen = true
    state.controlsPaneTab = RightPaneTab.Parameters
  },
  updateSqlParameter: (state: BuilderState, action: PayloadAction<SqlParameter>) => {
    const index = state.sqlParameters.findIndex((p) => p.id === action.payload.id)
    if (index !== -1) {
      state.sqlParameters[index] = action.payload
    } else {
      state.sqlParameters.push(action.payload)
    }
  },
  removeSqlParameter: (state: BuilderState, action: PayloadAction<SqlParameter>) => {
    state.sqlParameters = state.sqlParameters.filter((p) => p.id !== action.payload.id)
  },
  setCurrentMapLockStatus(state: BuilderState, action: PayloadAction<BuilderLockState | undefined>) {
    state.currentMapLockStatus = action.payload
  },
  updateCurrentMapLockStatus(state: BuilderState, action: PayloadAction<Partial<BuilderLockState>>) {
    state.currentMapLockStatus = { locked: false, ...state.currentMapLockStatus, ...action.payload }
  },
  setTakeOverResponseState(state: BuilderState, action: PayloadAction<TakeOverResponseState>) {
    state.takeOverResponseState = action.payload
  },
  setLayerUpdateStatus(state: BuilderState, action: PayloadAction<{ layerId: string; status: RequestStatus }>) {
    state.layersUpdateStatus = {
      ...state.layersUpdateStatus,
      [action.payload.layerId]: action.payload.status
    }
  },
  setViewerCredentialsRequired(state: BuilderState, action: PayloadAction<Connection[]>) {
    state.viewerCredentialsRequired = action.payload
  },
  setUserLocation(state: BuilderState, action: PayloadAction<GeoJSON.Position | undefined>) {
    state.userLocation = action.payload
  },
  disableMeasureTool(state: BuilderState) {
    if (state.isMeasureToolEnabled) {
      state.isMeasureToolEnabled = false
      state.measureToolLayer = undefined
      state.measureToolTooltips = undefined
      state.measureToolGuides = undefined
      state.measureToolPopup = undefined
    }
  },
  setIsMeasureToolEnabled(state: BuilderState, action: PayloadAction<boolean>) {
    state.isMeasureToolEnabled = action.payload
  },
  setMeasureToolLayer(state: BuilderState, action: PayloadAction<FeatureCollection | undefined>) {
    state.measureToolLayer = action.payload
  },
  setMeasureToolTooltips(state: BuilderState, action: PayloadAction<MeasureTooltip[] | undefined>) {
    state.measureToolTooltips = action.payload
  },
  setMeasureToolGuides(state: BuilderState, action: PayloadAction<FeatureCollection | undefined>) {
    state.measureToolGuides = action.payload
  },
  setMeasureToolPopup(state: BuilderState, action: PayloadAction<MeasureToolPopup | undefined>) {
    state.measureToolPopup = action.payload
  },
  addTimeSeriesDataSourceAnimating(state: BuilderState, action: PayloadAction<string>) {
    state.timeSeriesDataSourceAnimating[action.payload] = true
  },
  removeTimeSeriesDataSourceAnimating(state: BuilderState, action: PayloadAction<string>) {
    delete state.timeSeriesDataSourceAnimating[action.payload]
  },
  setGeocoderSearchText(state: BuilderState, action: PayloadAction<string>) {
    state.geocoderSearchText = action.payload
  },
  setBasemapSettings(state: BuilderState, action: PayloadAction<BasemapSettings>) {
    state.basemapSettings = action.payload
  },
  setCustomBasemaps(state: BuilderState, action: PayloadAction<Basemap[]>) {
    state.customBasemaps = action.payload
  },
  setMapStyles(state: BuilderState, action: PayloadAction<KeplerMapStyle[]>) {
    state.mapStyles = action.payload
  },
  // TODO: This shouldn't be part of the builder state, but a local state of the React component responsible for displaying the LayerPanel (even a Context or just a drilled prop)
  // instead, we need this workaround because there's no mechanism for determining from the React component side, if the Saga has finished or not.
  setLayerPanelLoadingStatus(state: BuilderState, action: PayloadAction<RequestStatus>) {
    state.layerPanelLoadingStatus = action.payload
  },
  setMapStylesStatus(state: BuilderState, action: PayloadAction<RequestStatus>) {
    state.mapStylesStatus = action.payload
  },
  setTakeMapScreenshots: (state: BuilderState, action: PayloadAction<boolean>) => {
    const takeMapScreenshots = action.payload
    // Multiple process could require to take map screenshots at the same time and if the map is splitted we could have the
    // first screenshot ready and the second in process.
    if (takeMapScreenshots && !state.takeMapScreenshots) {
      state.mapScreenshots = []
    }
    state.takeMapScreenshots = takeMapScreenshots
  },
  setMapScreenshot: (state: BuilderState, action: PayloadAction<{ index: number; screenshot: string }>) => {
    state.mapScreenshots[action.payload.index] = action.payload.screenshot
  },
  updateAISettings: (state: BuilderState, action: PayloadAction<Partial<AISettings>>) => {
    state.aiSettings = {
      ...state.aiSettings,
      ...action.payload
    }
  },
  setGenAIThread: (state: BuilderState, action: PayloadAction<BuilderGenAIThread>) => {
    state.genAiThread = action.payload
  },
  addGenAIMessage: (state: BuilderState, action: PayloadAction<BuilderGenAIConversation[number]>) => {
    const { author, message } = action.payload
    const lastMessage = state.genAiConversation[state.genAiConversation.length - 1]
    const shouldMergeBotMessages =
      lastMessage &&
      lastMessage.author === BuilderGenAIAuthor.Bot &&
      typeof lastMessage.message !== 'string' &&
      author === BuilderGenAIAuthor.Bot &&
      typeof message !== 'string'

    if (shouldMergeBotMessages) {
      ;(lastMessage.message as BuilderGenAIAction[]).push(...message)
    } else {
      state.genAiConversation.push(action.payload)
    }
  },
  nextGenAIAction: (state: BuilderState) => {
    const conversation = state.genAiConversation
    const action = getFirstGenAIActionNotCompleted(conversation)
    if (action) {
      action.isCompleted = true
    }
  },
  cleanGenAIConversation: (state: BuilderState) => {
    state.genAiThread = undefined
    state.genAiConversation = []
  },
  setDynamicTilingMigrationBackup: (state: BuilderState, action: PayloadAction<DynamicTilingMigrationBackup>) => {
    state.dynamicTilingMigrationBackup = action.payload
  },
  setDragMapState: (state: BuilderState, action: PayloadAction<Partial<MapState> | undefined>) => {
    state.dragMapState = action.payload ? { ...state.dragMapState, ...action.payload } : undefined
  },
  setHighlighted: (
    state: BuilderState,
    action: PayloadAction<{ id: string | null; type: 'dataset' | 'layer' | null }>
  ) => {
    state.highlighted = action.payload
  }
}

function getFirstGenAIActionNotCompleted(conversation: BuilderGenAIConversation) {
  for (const { message } of conversation) {
    const action = typeof message !== 'string' && message.find((action) => !action.isCompleted)
    if (action) {
      return action
    }
  }
}

export const builderGenAIActionToExecute = createSelector(
  (state: RootState) => state.builder.genAiConversation,
  (conversation) => getFirstGenAIActionNotCompleted(conversation)
)

export const someTimeSeriesDataSourceAnimating = createSelector(
  (state: RootState) => state.builder.timeSeriesDataSourceAnimating,
  (timeSeriesDataSourceAnimating) => {
    for (const animating of Object.values(timeSeriesDataSourceAnimating)) {
      if (animating) return true
    }
    return false
  }
)

export const allDatasetsLoadedAndHaveLayers = createSelector(
  (state: RootState) => state.maps.currentMapDatasets,
  (state: RootState) => state.keplerGl.map?.visState?.layers,
  (datasets, layers) => {
    const layersDataIds = layers ? layers.map((l) => l.config.dataId) : []
    return datasets.every((d) => d.status === RequestStatus.Fulfilled && layersDataIds.includes(d.id))
  }
)

export const isAutosavingEnabledSelector = createSelector(
  allDatasetsLoadedAndHaveLayers,
  someTimeSeriesDataSourceAnimating,
  blockAutoSaveDocMigrationTemporary,
  (state) => state.builder.isLoading,
  (state: RootState) => state.builder.builderMode,
  (state: RootState) => state.builder.previewMode,
  (
    allDatasetsLoadedAndHaveLayers,
    someTimeSeriesDataSourceAnimating,
    blockAutoSaveDocMigrationTemporary,
    isLoading,
    builderMode,
    previewMode
  ) => {
    return Boolean(
      (builderMode === BuilderMode.Editor || previewMode) &&
        !isLoading &&
        !blockAutoSaveDocMigrationTemporary &&
        allDatasetsLoadedAndHaveLayers &&
        !someTimeSeriesDataSourceAnimating
    )
  }
)

export const getLayerUpdateStatus = createSelector(
  (state: RootState) => state.builder.layersUpdateStatus,
  (_: unknown, layerId: string) => layerId,
  (layersUpdateStatus, layerId) => layersUpdateStatus[layerId] || RequestStatus.Idle
)

export const widgetsMapConfigSelector = createSelector(
  (state: RootState) => state.builder.widgets,
  (state: RootState) => state.carto.dataSources,
  selectValidSpatialFilter,
  (allWidgets, cartoDataSources, spatialFilter) => {
    // See: https://github.com/CartoDB/cloud-native/pull/17086
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const widgets = allWidgets.filter((w) => w.isValid).map(({ state, ...w }) => w)
    const filters = Object.keys(cartoDataSources).reduce((filters, dataSource) => {
      const _filters = cartoDataSources[dataSource].filters || {}
      const filtersWithoutTimeType = getFiltersWithoutTimeType(_filters)

      if (Object.keys(filtersWithoutTimeType).length >= 0) {
        filters[dataSource] = filtersWithoutTimeType
      }
      return filters
    }, {})

    return { widgets, filters, spatialFilter } as WidgetsMapConfig
  }
)

export const builderPersistedStateSelector = createSelector(
  (state: RootState) => state.builder,
  (state: RootState) => state.mapComments.commentsVisible,
  widgetsMapConfigSelector,
  (builderState, commentsVisible, { widgets, filters, spatialFilter }) => {
    const {
      customBaseMaps,
      legendSettings,
      popupSettings,
      sqlParameters,
      descriptionOpen,
      descriptionPreview,
      controlsPaneOpen,
      controlsPaneTab,
      aiSettings,
      dynamicTilingMigrationBackup
    } = builderState

    return {
      widgets,
      filters,
      spatialFilter,
      ...(customBaseMaps !== undefined && { customBaseMaps }),
      ...(legendSettings !== undefined && { legendSettings }),
      popupSettings,
      sqlParameters,
      aiSettings,
      uiState: {
        descriptionOpen,
        descriptionPreview,
        controlsPaneOpen,
        ...(controlsPaneTab !== undefined && { controlsPaneTab }),
        commentsVisible
      },
      dynamicTilingMigrationBackup
    }
  }
)

export const mapConfigSelector = createSelector(
  (state: RootState) => state.keplerGl.map,
  builderPersistedStateSelector,
  (state: RootState) => state.maps.mapSettings,
  defaultMapStyleSelector,
  (keplerGlState, persistedBuilderState, mapSettings, defaultMapStyle) => {
    // @ts-expect-error TODO
    const keplerConfig: CartoKeplerMapConfigEnvelope = KeplerGlSchema.getConfigToSave(keplerGlState)
    return {
      ...keplerConfig,
      config: {
        ...keplerConfig.config,
        visState: {
          ...keplerConfig.config.visState,
          layers: updateLayerCustomMarkers(keplerConfig)
        },
        mapStyle: {
          ...keplerConfig.config.mapStyle,
          styleType: keplerConfig.config.mapStyle.styleType || defaultMapStyle?.id || DEFAULT_MAP_STYLE
        },
        mapSettings,
        ...persistedBuilderState
      }
    }
  }
)

export const mapConfigHashSelector = createSelector(mapConfigSelector, getValueHash)

export function filterRightPaneWidgets(widgets: Widget[]): Widget[] {
  return widgets.filter((w) => {
    if (w.type === WidgetType.TIMESERIES) {
      return widgets.some((otherWidget) => otherWidget.type === WidgetType.TABLE)
    }
    return w.type !== WidgetType.TABLE
  })
}

export function filterBottomPaneWidgets(widgets: Widget[]): Widget[] {
  return widgets.filter((w) => {
    if (w.type === WidgetType.TIMESERIES) {
      return widgets.some((otherWidget) => otherWidget.type !== WidgetType.TABLE)
    }
    return w.type === WidgetType.TABLE
  })
}

export const builderSlice = createSlice({
  name,
  initialState,
  reducers
})

export const {
  setMapPasswordRequired,
  incMapPasswordEnterAttempt,
  showSQLPanel,
  setSQLPanelMinimized,
  hideSQLPanel,
  setDraftQuery,
  resetBuilderState,
  setIsLoading,
  setSaveNeeded,
  resetSaveNeeded,
  setSaveMapError,
  setBuilderMode,
  setPreviewMode,
  setLastThumbnailMapConfigHash,
  setWidgets,
  removeWidget,
  updateWidgetProps,
  updateWidgetWrapperProps,
  replaceWidget,
  removeWidgetsByDataSource,
  setSelectedWidgetId,
  setTilesetWidgetSourceReady,
  setControlsPaneOpen,
  setControlsPaneState,
  setRightPaneTab,
  setCustomBaseMap,
  setCustomBaseMapSettings,
  setLegendSettings,
  setLayerLegendSettings,
  addDatasetAttributeStatsOnRequest,
  removeDatasetAttributeStatsOnRequest,
  setPopupSettings,
  setLayerPopupSettings,
  moveLayerPopupSettings,
  setFeatureDetailsPanel,
  closeFeatureDetailsPanel,
  loadSqlParameters,
  addSqlParameter,
  updateSqlParameter,
  removeSqlParameter,
  setCurrentMapLockStatus,
  updateCurrentMapLockStatus,
  setTakeOverResponseState,
  setLayerUpdateStatus,
  setViewerCredentialsRequired,
  setUserLocation,
  setDescriptionOpen,
  setDescriptionPreview,
  setIsMeasureToolEnabled,
  setMeasureToolLayer,
  setMeasureToolTooltips,
  setMeasureToolGuides,
  setMeasureToolPopup,
  disableMeasureTool,
  addTimeSeriesDataSourceAnimating,
  removeTimeSeriesDataSourceAnimating,
  setGeocoderSearchText,
  setLayerPanelLoadingStatus,
  setBasemapSettings,
  setCustomBasemaps,
  setMapStyles,
  setMapStylesStatus,
  setTakeMapScreenshots,
  setMapScreenshot,
  updateAISettings,
  setGenAIThread,
  addGenAIMessage,
  nextGenAIAction,
  cleanGenAIConversation,
  setDynamicTilingMigrationBackup,
  setDragMapState,
  setHighlighted
} = builderSlice.actions

export const builderSliceActions = builderSlice.actions

export const {
  initBuilderMap,
  addLayerRequest,
  addLayerToExistingDatasetRequest,
  addQueryRequest,
  updateDatasetsWithSqlParametersRequest,
  updateQueryRequest,
  cancelQueryRequest,
  refreshDatasetRequest,
  refreshDatasetsByConnections,
  attachViewerCredentials,
  addLayerFromImportedJobRequest,
  calculateFeaturesForTilesetRequest,
  newWidget,
  removeFilterByWidgetRequest,
  loadAttributeStatsRequest,
  loadHexColumnStatsRequest,
  forceCalculateDatasourceFeatures,
  updateSpatialIndexAggregationRequest,
  unlockMapRequest,
  updateSpatialIndexAggregationResLevelRequest,
  queryThisTableRequest,
  convertTableToQueryDatasetRequest,
  aggregateDatasetByGeometry,
  updateLayerFiltersForTimeSeriesAnimationRequest,
  mapPasswordUpdated,
  legendVisibilityChange
} = asyncActions

export const getWidgetsByDataset = createSelector(
  (state: RootState) => state.builder.widgets,
  (_: unknown, datasetId: string) => datasetId,
  (widgets, datasetId) => widgets.filter((w) => w.dataSource === datasetId)
)

export const builderDatasetsSelector = createSelector(
  (state: RootState) => state.maps.currentMapDatasets,
  (currentMapDatasets) => {
    return currentMapDatasets.map(
      (mapDataset, index) =>
        ({
          ...mapDataset,
          letter: String.fromCharCode(65 + index),
          color: mapDataset.color || getNewDatasetColor(currentMapDatasets)
        } as BuilderDataset)
    )
  }
)

export const getBuilderDatasetForLayer = createSelector(
  builderDatasetsSelector,
  (_: unknown, layer: Layer) => layer,
  (datasets, layer): BuilderDataset | undefined => {
    return datasets.find((dataset) => dataset.id === layer.config.dataId)
  }
)

// TODO: Find a proper place for this
const keplerDatasets = (state: RootState) => state.keplerGl.map?.visState?.datasets
const datasets = (state: RootState) => state.maps.currentMapDatasets

export const getBuilderDatasetById = createSelector(
  [builderDatasetsSelector, (_: unknown, datasetId: string) => datasetId],
  (datasets, datasetId): BuilderDataset | undefined => {
    return datasets.find((d) => d.id === datasetId)
  }
)

export const getEnrichedDatasetById = createSelector(
  [keplerDatasets, datasets, (_: unknown, datasetId: string) => datasetId],
  (keplerDatasets, datasets, datasetId): MapDatasetEnriched | null => {
    const dataset = datasets.find((d) => d.id === datasetId)
    return datasetId in keplerDatasets ? { ...dataset, ...keplerDatasets[datasetId] } : null
  }
)

export const isMapToolEnabledSelector = createSelector(
  (state: RootState) => state.carto.featureSelectionEnabled,
  (state: RootState) => state.builder.isMeasureToolEnabled,
  (state: RootState) => state.mapComments.commentsOpen,
  (featureSelectionEnabled, isMeasureToolEnabled, commentsOpen) =>
    featureSelectionEnabled || isMeasureToolEnabled || commentsOpen
)

export default builderSlice.reducer
